import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Styles from '@/assets/js/Styles';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import { Typography } from '@mui/material';
import { useControlledFieldArray } from '../../../hooks/useControlledFieldArray';
import { BlockPath } from '../../../editor/PageEditor/CurrentBlockProvider';
import { BlockSpecificPage } from '../../../editor/lib/declarations/BlockSpecificPage';
import { HeaderMenuBlock } from '../../../declarations/models/blocks/settingsBlocks/HeaderMenuBlock';
import Links from '../../../components/Links/Links';
import { LinkType } from '../../../declarations/models/LinkType';
import LinkListItem from '../../../components/Links/LinkListItem';
import { Draggable } from '../../../declarations/Draggable';
import Container from '../../../components/Container';
import TextInput from '../../../components/forms/TextInput';
import LanguageMenuMainMenuSubMenuSettings from './LanguageMenuMainMenuSubMenuSettings';
import SwitchInput from '../../../components/forms/SwitchInput';

export interface LanguageMenuMainMenuSettingsProps {
  blockPath: BlockPath;
}

export const LanguageMenuMainMenuSettings: FC<LanguageMenuMainMenuSettingsProps> = ({ blockPath }) => {
  const { t } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const [itemVisible, setItemVisible] = useState<boolean>(true);

  const {
    fields: items,
    append,
    update,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<HeaderMenuBlock>, `${BlockPath}.items`>({ name: `${blockPath}.items` });

  const handleMove = (fromIndex: number, toIndex: number) => {
    // Its critical that settings are hidden during move to prevent rendering issues!!!!!!!!!!!!!!!!!!!!!!!!!!
    setItemVisible(false);
    move(fromIndex, toIndex);
    setTimeout(() => {
      // Re-show settings after move is done
      setItemVisible(true);
    }, 500);
  };

  const handleRemove = (index: number) => {
    setItemVisible(false);
    remove(index);
    setTimeout(() => {
      // Re-show settings after move is done
      setItemVisible(true);
    }, 200);
  };

  return (
    <>
      {items?.map(
        (item, i) =>
          itemVisible && (
            <LinkListItem
              key={item.id ? `${item.id}_${i}` : `${item.url || item.title || ''}_${i}`} // eslint-disable-line react/no-array-index-key
              linkType={item.type as LinkType}
              type={Draggable.OTHER}
              index={i}
              onReorder={handleMove}
              onDelete={handleRemove}
              itemsArrayName='items'
              showLinkDescriptionField={false}
              onChangeFile={() => null}
              blockPath={blockPath}
              iconOverride={'status' in item ? <PageStatusCircle status={item.status} size={20} /> : undefined}
              headerSubTitle={
                <>
                  {item.hidden && <Typography variant='overline'>({tCommon('hidden')})</Typography>}
                  <Typography sx={{ fontSize: 'small' }}>
                    {t('Settings.LanguageSettings.childCount', { count: item.items?.length ?? 0 })}
                  </Typography>
                </>
              }
              color={Styles.Colors.LIGHT_GREY}>
              {itemVisible && (
                <Container fullWidth p={2} column>
                  <Container fullWidth>
                    <TextInput
                      path={`${blockPath}.items.${i}.title`}
                      label={t('Settings.LanguageSettings.LanguageMenuSettings.menuPointTitle')}
                      defaultValue=''
                    />
                    <TextInput
                      path={`${blockPath}.items.${i}.anchor`}
                      label={t('LinkListItem.Anchor')}
                      defaultValue=''
                      InputProps={{
                        startAdornment: <Typography color={Styles.Colors.MEDIUM_GREY}>#</Typography>,
                      }}
                    />
                    <TextInput
                      path={`${blockPath}.items.${i}.cssClass`}
                      label={t('Settings.LanguageSettings.LanguageMenuSettings.cssClass')}
                      defaultValue=''
                    />
                    <SwitchInput path={`${blockPath}.items.${i}.hidden`} label={tCommon('hide')} defaultValue={false} />
                  </Container>
                  <Container fullWidth>
                    {item.type === LinkType.LINK && (
                      <TextInput
                        path={`${blockPath}.items.${i}.url`}
                        label={t('Settings.LanguageSettings.LanguageMenuSettings.url')}
                        defaultValue=''
                      />
                    )}
                  </Container>

                  <LanguageMenuMainMenuSubMenuSettings itemPath={`${blockPath}.items.${i}`} />
                </Container>
              )}
            </LinkListItem>
          ),
      )}

      <Links
        onUpdate={update}
        onAppend={append}
        onRemove={remove}
        onReorder={move}
        showLinkItems={false}
        showLinkDescriptionField={false}
        showLinkStyleSelector={false}
        showListChildrenInput={false}
        linkItems={items}
        linkTypes={[LinkType.PAGE, LinkType.LINK]}
        blockPath={blockPath}
      />
    </>
  );
};

export default LanguageMenuMainMenuSettings;
